// components/reports/ReportLayout.tsx
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Card, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, CircularProgress, alpha, Tooltip, IconButton,
} from '@mui/material';
import {
  FileDownload as ExcelIcon, PictureAsPdf as PdfIcon,
  Refresh as RefreshIcon,
  KeyboardArrowUp, KeyboardArrowDown,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { fmtBD } from '../common/bdNumber';

export interface ReportColumn {
  key:      string;
  label:    string;
  align?:   'left' | 'right' | 'center';
  format?:  (v: any, row: any) => React.ReactNode;
  width?:   number;
  noWrap?:  boolean;
}

export interface SummaryCard {
  label:   string;
  value:   string | number;
  color:   string;
  icon?:   React.ReactNode;
  prefix?: string;
}

interface ReportLayoutProps {
  title:          string;
  subtitle?:      string;
  accentColor:    string;
  columns:        ReportColumn[];
  rows:           any[];
  loading:        boolean;
  summaryCards?:  SummaryCard[];
  filters:        React.ReactNode;
  onRefresh:      () => void;
  onDownload?:    (fmt: 'excel' | 'pdf') => void;
  totalsRow?:     React.ReactNode;
  emptyIcon?:     React.ReactNode;
  tableMinWidth?: number;
}

const useIsDataManager = () => {
  const { user } = useAuth();
  return user?.is_superuser || user?.role?.role_name === 'DATA_MANAGER';
};

export const fmt2 = (v: string | number | null | undefined): string => {
  if (v === null || v === undefined || v === '') return '—';
  return fmtBD(v, 0);   // BD grouping, whole numbers — matches current behaviour
};

export const fmtDate = (d: string): string => {
  if (!d) return '—';
  const dt    = new Date(d + 'T00:00:00');
  const day   = String(dt.getDate()).padStart(2, '0');
  const month = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][dt.getMonth()];
  return `${day}-${month}-${dt.getFullYear()}`;
};

const ReportLayout: React.FC<ReportLayoutProps> = ({
  title, subtitle, accentColor, columns, rows, loading,
  summaryCards, filters, onRefresh, onDownload, totalsRow,
  emptyIcon, tableMinWidth = 1000,
}) => {
  const isDataManager = useIsDataManager();
  const [dlLoading, setDlLoading] = useState<'excel' | 'pdf' | null>(null);

  // ── Scroll arrow-rail state ──────────────────────────────────────────────
  const scrollRef  = useRef<HTMLDivElement | null>(null);
  const holdDelay  = useRef<ReturnType<typeof setTimeout>  | null>(null);
  const holdTimer  = useRef<ReturnType<typeof setInterval> | null>(null);
  const [scroll, setScroll] = useState({ up: false, down: false, overflow: false });

  const updateScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const overflow = el.scrollHeight - el.clientHeight > 2;
    setScroll({
      overflow,
      up:   el.scrollTop > 2,
      down: overflow && el.scrollTop < el.scrollHeight - el.clientHeight - 2,
    });
  }, []);

  // recompute when data changes and whenever the container resizes
  useEffect(() => {
    updateScroll();
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(updateScroll);
    ro.observe(el);
    return () => ro.disconnect();
  }, [updateScroll, rows, loading]);

  const scrollByStep = (dy: number, smooth = true) =>
    scrollRef.current?.scrollBy({ top: dy, behavior: smooth ? 'smooth' : 'auto' });

  const stopHold = useCallback(() => {
    if (holdDelay.current) { clearTimeout(holdDelay.current);  holdDelay.current = null; }
    if (holdTimer.current) { clearInterval(holdTimer.current); holdTimer.current = null; }
  }, []);

  const startHold = (dir: 1 | -1) => {
    stopHold();
    scrollByStep(dir * 140);                       // one step immediately (covers a plain click)
    holdDelay.current = setTimeout(() => {          // after a short hold, repeat continuously
      holdTimer.current = setInterval(() => scrollByStep(dir * 40, false), 30);
    }, 300);
  };

  useEffect(() => () => stopHold(), [stopHold]);    // cleanup on unmount

  // keyboard step (Enter/Space) — mouse uses onMouseDown so clicks don't double-step
  const arrowKey = (dir: 1 | -1) => (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); scrollByStep(dir * 160); }
  };

  const handleDownload = async (fmt: 'excel' | 'pdf') => {
    if (!onDownload) return;
    setDlLoading(fmt);
    try { await onDownload(fmt); } finally { setDlLoading(null); }
  };

  const arrowBtnSx = {
    borderRadius: 0,
    color: accentColor,
    '&:hover': { background: alpha(accentColor, 0.14) },
    '&.Mui-disabled': { color: alpha(accentColor, 0.25) },
  } as const;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>

      {/* ── Header ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 1.5 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{
            background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}99 100%)`,
            backgroundClip: 'text', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          }}>
            {title}
          </Typography>
          {subtitle && <Typography variant="body2" color="text.secondary" mt={0.25}>{subtitle}</Typography>}
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <Tooltip title="Refresh">
            <IconButton onClick={onRefresh} size="small"
              sx={{ border: `1px solid ${alpha(accentColor, 0.3)}`, color: accentColor }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {isDataManager && onDownload && (
            <>
              <Button size="small" variant="outlined"
                startIcon={dlLoading === 'excel' ? <CircularProgress size={14} /> : <ExcelIcon />}
                onClick={() => handleDownload('excel')} disabled={!!dlLoading || loading}
                sx={{ borderRadius: 2, borderColor: '#4CAF50', color: '#4CAF50', '&:hover': { background: alpha('#4CAF50', 0.06) } }}>
                Excel
              </Button>
              <Button size="small" variant="outlined"
                startIcon={dlLoading === 'pdf' ? <CircularProgress size={14} /> : <PdfIcon />}
                onClick={() => handleDownload('pdf')} disabled={!!dlLoading || loading}
                sx={{ borderRadius: 2, borderColor: '#f44336', color: '#f44336', '&:hover': { background: alpha('#f44336', 0.06) } }}>
                PDF
              </Button>
            </>
          )}
        </Stack>
      </Box>

      {/* ── Summary Cards ── */}
      {summaryCards && summaryCards.length > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flexWrap="wrap">
          {summaryCards.map(c => (
            <Card key={c.label} elevation={0} sx={{
              flex: 1, minWidth: 150, p: 1.75, borderRadius: 3, whiteSpace: 'pre-line',
              border: `1px solid ${alpha(c.color, 0.2)}`,
              background: `linear-gradient(135deg, ${alpha(c.color, 0.07)} 0%, ${alpha(c.color, 0.02)} 100%)`,
              display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'center', textAlign: 'center',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: `0 4px 20px ${alpha(c.color, 0.18)}` },
            }}>
              {c.icon && (
                <Box sx={{ width: 40, height: 40, borderRadius: '10px', background: alpha(c.color, 0.14),
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: c.color, flexShrink: 0 }}>
                  {c.icon}
                </Box>
              )}
              <Box>
                <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ letterSpacing: '0.03em' }}>
                  {c.label}
                </Typography>
                <Typography fontWeight={800} sx={{ color: c.color, lineHeight: 1.2, fontSize: '1rem' }}>
                  {c.prefix ?? ''}{c.value}
                </Typography>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      {/* ── Filters ── */}
      <Card sx={{ p: 2, borderRadius: 3, border: `1px solid ${alpha(accentColor, 0.1)}` }} elevation={0}>
        {filters}
      </Card>

      {/* ── Table Card ── */}
      <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${alpha(accentColor, 0.12)}`, overflow: 'hidden' }}>

        {/* record count bar */}
        <Box sx={{
          px: 2.5, py: 1.25,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          borderBottom: `1px solid ${alpha(accentColor, 0.08)}`,
          background: alpha(accentColor, 0.03),
        }}>
          <Typography variant="caption" fontWeight={700} sx={{ color: accentColor, letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.7rem' }}>
            Report Data
          </Typography>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            {rows.length} record{rows.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        {/* scrollable table + arrow rail */}
        <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
          <TableContainer
            ref={scrollRef}
            onScroll={updateScroll}
            sx={{
              flex: 1,
              minWidth: 0,                       // let flex child shrink so overflowX works
              maxHeight: 'calc(100vh - 340px)',
              overflowX: 'auto',
              overflowY: 'auto',
              // custom scrollbar
              '&::-webkit-scrollbar':       { width: 6, height: 6 },
              '&::-webkit-scrollbar-track': { background: '#f1f1f1', borderRadius: 10 },
              '&::-webkit-scrollbar-thumb': { background: alpha(accentColor, 0.4), borderRadius: 10 },
              '&::-webkit-scrollbar-thumb:hover': { background: alpha(accentColor, 0.7) },
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{ minWidth: tableMinWidth, borderCollapse: 'separate', borderSpacing: 0 }}
            >
              {/* ── Head ── */}
              <TableHead>
                <TableRow>
                  {columns.map((col, ci) => (
                    <TableCell key={col.key} sx={{
                      fontWeight: 700,
                      fontSize: '0.72rem',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                      color: '#fff',
                      py: 1.4,
                      px: 1.5,
                      whiteSpace: 'nowrap',
                      textAlign: col.align ?? 'left',
                      width:    col.width ?? undefined,
                      minWidth: col.width ?? 90,
                      position: 'sticky',
                      top: 0,
                      zIndex: 3,
                      background: `linear-gradient(135deg, ${accentColor} 0%, ${accentColor}cc 100%)`,
                      borderRight: ci < columns.length - 1 ? '1px solid rgba(255,255,255,0.15)' : 'none',
                      borderBottom: 'none',
                      // rounded corners on first/last
                      ...(ci === 0                    && { borderTopLeftRadius: 0 }),
                      ...(ci === columns.length - 1   && { borderTopRightRadius: 0 }),
                    }}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              {/* ── Body ── */}
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 6, border: 'none' }}>
                      <CircularProgress size={32} sx={{ color: accentColor }} />
                      <Typography variant="caption" display="block" color="text.secondary" mt={1}>Loading data…</Typography>
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 8, border: 'none' }}>
                      {emptyIcon && <Box sx={{ mb: 1.5, color: 'text.disabled' }}>{emptyIcon}</Box>}
                      <Typography variant="body2" color="text.secondary" fontWeight={500}>No data found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <>
                    {rows.map((row, idx) => (
                      <TableRow
                        key={idx}
                        sx={{
                          // alternating rows
                          background: idx % 2 === 0 ? '#ffffff' : alpha(accentColor, 0.025),
                          transition: 'background 0.15s, box-shadow 0.15s',
                          '&:hover': {
                            background: alpha(accentColor, 0.09),
                            boxShadow: `inset 3px 0 0 ${accentColor}`,
                          },
                          // no bottom border on last row before totals
                          '&:last-child td': { borderBottom: 'none' },
                        }}
                      >
                        {columns.map((col, ci) => (
                          <TableCell key={col.key} sx={{
                            py: 1.15,
                            px: 1.5,
                            fontSize: '0.8rem',
                            color: 'text.primary',
                            textAlign: col.align ?? 'left',
                            whiteSpace: col.noWrap ? 'nowrap' : 'normal',
                            minWidth: col.width ?? 90,
                            borderBottom: `1px solid ${alpha(accentColor, 0.07)}`,
                            borderRight: ci < columns.length - 1 ? `1px solid ${alpha(accentColor, 0.05)}` : 'none',
                            // word-wrap for long text columns
                            wordBreak: (!col.noWrap && !col.align) ? 'break-word' : undefined,
                          }}>
                            {col.format ? col.format(row[col.key], row) : (row[col.key] ?? '—')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}

                    {/* Totals row */}
                    {totalsRow}
                  </>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* ── Vertical arrow rail (only when the table overflows) ── */}
          {scroll.overflow && (
            <Stack
              sx={{
                width: 30,
                flexShrink: 0,
                borderLeft: `1px solid ${alpha(accentColor, 0.12)}`,
                background: alpha(accentColor, 0.03),
              }}
            >
              <Tooltip title="Scroll up" placement="left">
                <span>
                  <IconButton
                    size="small"
                    disabled={!scroll.up}
                    aria-label="Scroll up"
                    onKeyDown={arrowKey(-1)}
                    onMouseDown={() => startHold(-1)}
                    onMouseUp={stopHold}
                    onMouseLeave={stopHold}
                    onTouchStart={() => startHold(-1)}
                    onTouchEnd={stopHold}
                    sx={arrowBtnSx}
                  >
                    <KeyboardArrowUp fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>

              <Box sx={{ flex: 1 }} />

              <Tooltip title="Scroll down" placement="left">
                <span>
                  <IconButton
                    size="small"
                    disabled={!scroll.down}
                    aria-label="Scroll down"
                    onKeyDown={arrowKey(1)}
                    onMouseDown={() => startHold(1)}
                    onMouseUp={stopHold}
                    onMouseLeave={stopHold}
                    onTouchStart={() => startHold(1)}
                    onTouchEnd={stopHold}
                    sx={arrowBtnSx}
                  >
                    <KeyboardArrowDown fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          )}
        </Box>
      </Card>
    </Box>
  );
};

export default ReportLayout;